// utils/permissions.js
import Axios from "../utils/Axios";
import { PERMISSION_LIST } from "./API_Collection";

// normalize codes like "p_e-003" -> "p_e_003"
const normalize = (code) =>
  code ? String(code).toLowerCase().replace(/-/g, "_").trim() : "";

/**
 * Build a nested permission map from the new API shape.
 *
 * @param {string[]} userCodes - e.g. ["e_c_002","e_c_003","e_d_004",...]
 * @returns {Promise<Object>}  -> { [module]: { [action]: boolean } }
 *
 * Example result:
 * {
 *   employees: { add_employee: true, edit_employee: true, delete_employee: false, ... },
 *   projects:  { project_list: true, edit_project: false, ... },
 *   ...
 * }
 */
export async function fetchAndBuildPermissions(userCodes = []) {
  // Normalize user codes once
  const userSet = new Set((userCodes || []).map(normalize).filter(Boolean));

  let resp;
  try {
    resp = await Axios.get(PERMISSION_LIST);

    // The new API returns an object like:
    // { employees: [{action, action_code}, ...], projects: [...], " budgets": [...] , ... }
    // It might be at resp.data or resp.data.data depending on your backend.
    const raw = resp?.data?.data ?? resp?.data ?? {};
    const modulesObj = raw && typeof raw === "object" ? raw : {};

    const result = {};

    // Iterate modules safely
    for (const [rawModule, list] of Object.entries(modulesObj)) {
      const moduleKey = String(rawModule).trim().toLowerCase(); // handles " budgets" -> "budgets"
      const items = Array.isArray(list) ? list : [];

      if (!result[moduleKey]) result[moduleKey] = {};

      for (const item of items) {
        const action = String(item?.action ?? "").trim();
        const code = normalize(item?.action_code);

        if (!action) continue; // skip malformed rows

        // true if user's normalized codes contain the normalized action_code
        result[moduleKey][action] = code ? userSet.has(code) : false;
      }
    }
    
    return result;
  } catch (error) {
    // Return a safe empty map on failure (or swap to throw if you prefer)
    return {};
  }
}
